'use client';

import { useEffect } from 'react';

import { playSfx, prefetchSfx } from '@/hooks/use-sound';

const EDITABLE_SELECTOR = 'input, textarea, [contenteditable="true"]';

/**
 * Input types where a keystroke actually enters text. Keeps typewriter sounds
 * off non-typing inputs (range sliders, checkboxes, radios, buttons, ...).
 */
const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'url',
  'password',
  'tel',
  'number',
]);

/** Elements activated with a click that plays 'click' (unless managed). */
const CLICKABLE_SELECTOR = 'button, [role="button"], a[href], summary';

/**
 * Invisible, mounted-once installer for global UI sound effects:
 *
 * - Plays a 'click' sound when any button/link/menu item is activated
 *   (mouse or keyboard), except elements marked [data-sfx-managed], which
 *   own a more specific sound (open/close/toggle).
 * - Plays a soft 'key' sound while typing in editable fields.
 * - Warms the sound-effect HTTP cache on the first user interaction
 *   (never earlier: zero requests and no AudioContext before a gesture).
 */
export function SoundEffects() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const clickable = target.closest(CLICKABLE_SELECTOR);
      if (!clickable) {
        return;
      }
      // Managed elements (collapsibles, chat FAB, theme toggle, menus, ...)
      // play their own open/close/toggle sound; don't double up.
      if (clickable.closest('[data-sfx-managed]')) {
        return;
      }
      playSfx('click');
    };

    const onKeydown = (event: KeyboardEvent) => {
      // A defaultPrevented key was consumed by a widget (e.g. cmdk's Enter
      // selecting an item), not typed as text — stay silent.
      if (
        event.defaultPrevented ||
        event.repeat ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }
      const key = event.key;
      if (key.length !== 1 && key !== 'Backspace' && key !== 'Enter') {
        return;
      }
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      const editable = target.closest(EDITABLE_SELECTOR);
      if (!editable) {
        return;
      }
      if (
        editable instanceof HTMLInputElement &&
        !TEXT_INPUT_TYPES.has(editable.type)
      ) {
        return;
      }
      playSfx('key');
    };

    const prefetch = () => {
      // Whichever event fires first wins; drop the other one-shot listener.
      document.removeEventListener('pointerdown', prefetch);
      document.removeEventListener('keydown', prefetch);
      prefetchSfx();
    };

    document.addEventListener('click', onClick, { passive: true });
    document.addEventListener('keydown', onKeydown, { passive: true });
    document.addEventListener('pointerdown', prefetch, {
      once: true,
      passive: true,
    });
    document.addEventListener('keydown', prefetch, {
      once: true,
      passive: true,
    });

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('pointerdown', prefetch);
      document.removeEventListener('keydown', prefetch);
    };
  }, []);

  return null;
}
